import { PluginListenerHandle, registerPlugin } from '@capacitor/core';

export interface RTKBridgePlugin {
  /**
   * Initialize the RealtimeKit meeting with authentication and configuration.
   * @param options - Meeting configuration options
   * @returns Promise resolving with initialization status
   */
  initialize(options: RTKInitializeOptions): Promise<{ initialized: boolean }>;

  /**
   * Start the meeting UI.
   * @returns Promise resolving with start status
   */
  startMeeting(): Promise<{ started: boolean }>;

  /**
   * End the current meeting.
   * @returns Promise resolving with end status
   */
  endMeeting(): Promise<{ ended: boolean }>;

  /**
   * Check if RealtimeKit is currently initialized.
   * @returns Promise resolving with initialization status
   */
  isInitialized(): Promise<{ initialized: boolean }>;

  /**
   * Listen to plugin lifecycle events.
   */
  addListener(
    eventName: 'meetingStarted' | 'meetingEnded',
    listenerFunc: () => void
  ): Promise<PluginListenerHandle>;
}

export interface RTKInitializeOptions {
  /**
   * Authentication token obtained from the Add Participant API.
   */
  authToken: string;

  /**
   * Enable microphone/audio in the meeting. Default: true
   */
  enableAudio?: boolean;

  /**
   * Enable camera/video in the meeting. Default: true
   */
  enableVideo?: boolean;

  /**
   * Optional base URL override for the RealtimeKit environment.
   */
  baseUrl?: string;
}

const RTKBridge = registerPlugin<RTKBridgePlugin>('RTKBridge', {
  web: async () => {
    const { RTKBridgeWeb } = await import('./RTKBridge.web');
    return new RTKBridgeWeb();
  },
});

export default RTKBridge;
